import type { RealtimeChannel } from '@supabase/supabase-js';
import { envConfig } from '@bg-shared/constants';
import { sessionStore } from '@bg-shared/business/Session/Session.store';
import { authConfig } from '../Auth/Auth.config';
import { supabaseService } from './Supabase.provider';
import { chatStoreSupabase } from './Chat.store';
import { chatApi } from './Chat.api';
import { ChatUtilities } from './Chat.utilities';
import {
    Infrastructure,
    IMessage,
    IMessageDetail,
    IProfile,
    IRoom,
    IStoreMessage,
} from './interfaces';

class ChatServiceSupabase {
    private messageChannels: Map<string, RealtimeChannel> = new Map();

    private roomChannels: Map<string, RealtimeChannel> = new Map();

    private SUBSCRIPTION_SYSTEM_MESSAGE_LIMIT = 2;

    private normalizeMessage(
        message: IMessage | IMessageDetail,
        profile: IProfile,
        errorMessage: string = null,
    ): IStoreMessage {
        return {
            ...message,
            display_name: profile.display_name,
            profile_status: profile.status,
            is_moderator: profile
                ? profile.role !== 'USER'
                : (message as IMessageDetail).is_moderator,
            user_id: profile.user_id,
            errorMessage,
        } as IStoreMessage;
    }

    private authHandler = supabaseService.authenticate.bind(supabaseService);

    public async login(): Promise<void> {
        chatStoreSupabase.setIsLoading(true);
        chatApi
            .login(authConfig.PlayerID)
            .then(this.authHandler)
            .catch((e) => {
                if (e.message.includes('nickname_not_found')) {
                    chatStoreSupabase.setIsLoading(false);
                    return;
                }
                ChatUtilities.handleError(e);
            });
    }

    public async register(nickname: string): Promise<void> {
        chatApi
            .register(authConfig.PlayerID, nickname)
            .then(this.authHandler)
            .catch((e) => {
                ChatUtilities.handleError(e, false);
            });
    }

    public async fetchMessages(roomId: string, limit = 50): Promise<IMessageDetail[]> {
        const { isLoggedIn, selfProfile } = chatStoreSupabase.value;

        if (!isLoggedIn || !selfProfile) {
            console.error('Attempted to fetch messages without being logged in');
            return [];
        }

        const client = supabaseService.getClient();

        const { data: messages, error } = await client
            .from('message_details')
            .select('*')
            .eq('room_id', roomId)
            .eq('is_deleted', false)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) {
            ChatUtilities.handleError(error);
            return [];
        }

        messages.forEach((message) => {
            if (message.profile_id && message.display_name) {
                chatStoreSupabase.updateProfile({
                    id: message.profile_id,
                    display_name: message.display_name,
                    status: message.profile_status,
                    role: message.is_moderator ? 'MODERATOR' : 'USER',
                } as IProfile);
            }
        });

        chatStoreSupabase.setMessages(messages.reverse());
        return messages as IMessageDetail[];
    }

    public async fetchRoomByGameId(gameId: number): Promise<IRoom> {
        const { isLoggedIn } = chatStoreSupabase.value;
        if (!isLoggedIn) {
            throw new Error('Must be logged in to fetch room');
        }

        const client = supabaseService.getClient();
        const { data, error } = await client.rpc('get_room_per_config', {
            p_game_id: gameId,
            p_partner_code: sessionStore.partnerCode,
            p_infrastructure: envConfig.environment.toUpperCase() as Infrastructure,
        });

        if (error) {
            ChatUtilities.handleError(error);
            return null;
        }

        const room = data as IRoom;
        chatStoreSupabase.setRoom(room);
        return room;
    }

    public async sendMessage(content: string): Promise<IMessageDetail | null> {
        const { isLoggedIn, selfProfile, room } = chatStoreSupabase.value;
        if (!isLoggedIn || !selfProfile) {
            throw new Error('Must be logged in to send messages');
        }

        const client = supabaseService.getClient();

        const { data: message, error } = await client
            .from('messages')
            .insert([
                {
                    room_id: room.id,
                    profile_id: selfProfile.id,
                    content,
                },
            ])
            .select();

        if (error) {
            chatStoreSupabase.addFailedMessage(content, error.code);
            return null;
        }

        const normalizedMessage = message?.[0]
            ? this.normalizeMessage(message[0], chatStoreSupabase.selfProfile)
            : null;

        return normalizedMessage;
    }

    // Helper method to wait for channel subscription
    private async waitForChannelSubscription(
        channel: RealtimeChannel,
        identifier: string,
        timeoutMs = 15000,
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // For some unknown reason the subscription status cb is not reliable to make sure the connection is actually stablished
            // In my tests the only way to make sure the WS connection is stablished is to check the system event
            // On second successful system event, the connection is surly stablished and ready to receive messages
            let counter = 0;

            const timeout = setTimeout(() => {
                reject(new Error(`Subscription timeout for ${identifier}`));
            }, timeoutMs);

            channel.on('system', {}, (payload) => {
                if (payload.status === 'ok' && payload.extension === 'postgres_changes') {
                    counter++;
                    if (counter >= this.SUBSCRIPTION_SYSTEM_MESSAGE_LIMIT) {
                        clearTimeout(timeout);
                        resolve();
                    }
                }
            });

            channel.subscribe();
        });
    }

    // Subscription methods
    public async subscribeToMessages(roomId: string): Promise<() => void> {
        const { isLoggedIn } = chatStoreSupabase.value;

        if (!isLoggedIn) {
            console.warn('Attempted to subscribe to messages without being logged in');
            return () => {};
        }

        if (!this.messageChannels.has(roomId)) {
            const channelName = `messages:${roomId}`;
            const channel = supabaseService.createChannel(channelName);

            channel.on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'messages',
                    filter: `room_id=eq.${roomId}`,
                },
                async (payload) => {
                    if (payload.eventType === 'DELETE') {
                        chatStoreSupabase.removeMessage(payload.old.id);
                        return;
                    }

                    const newMessage = payload.new as IMessage;
                    if (newMessage.is_deleted) {
                        chatStoreSupabase.removeMessage(newMessage.id);
                        return;
                    }

                    const profile = await supabaseService.getProfile(newMessage.profile_id);
                    const normalizedMessage = this.normalizeMessage(newMessage, profile);

                    if (payload.eventType === 'INSERT') {
                        chatStoreSupabase.addMessage(normalizedMessage);
                    } else if (payload.eventType === 'UPDATE') {
                        chatStoreSupabase.updateMessage(normalizedMessage.id, normalizedMessage);
                    }
                },
            );

            await this.waitForChannelSubscription(channel, channelName);

            this.messageChannels.set(roomId, channel);
        }

        return () => {
            const channel = this.messageChannels.get(roomId);
            if (channel) {
                supabaseService.getClient().removeChannel(channel);
                this.messageChannels.delete(roomId);
            }
        };
    }

    public async subscribeToProfile(profileId: string): Promise<() => void> {
        const { isLoggedIn } = chatStoreSupabase.value;

        if (!isLoggedIn) {
            console.warn('Attempted to subscribe to profile without being logged in');
            return () => {};
        }

        const channelName = `profile:${profileId}`;
        const channel = supabaseService.createChannel(channelName);

        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'profiles',
                filter: `id=eq.${profileId}`,
            },
            (payload) => {
                const profile = payload.new as IProfile;
                chatStoreSupabase.setSelfProfile(profile);
            },
        );

        await this.waitForChannelSubscription(channel, channelName);

        return () => {
            supabaseService.getClient().removeChannel(channel);
        };
    }

    public async subscribeToRoom(roomId: string): Promise<() => void> {
        const { isLoggedIn } = chatStoreSupabase.value;

        if (!isLoggedIn) {
            console.warn('Attempted to subscribe to room without being logged in');
            return () => {};
        }

        if (!this.roomChannels.has(roomId)) {
            const channelName = `room:${roomId}`;
            const channel = supabaseService.createChannel(channelName);

            channel.on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'rooms',
                    filter: `id=eq.${roomId}`,
                },
                (payload) => {
                    const room = payload.new as IRoom;
                    chatStoreSupabase.setRoom(room);
                },
            );

            await this.waitForChannelSubscription(channel, channelName);

            this.roomChannels.set(roomId, channel);
        }

        return () => {
            const channel = this.roomChannels.get(roomId);
            if (channel) {
                supabaseService.getClient().removeChannel(channel);
                this.roomChannels.delete(roomId);
            }
        };
    }
}

export const chatServiceSupabase = new ChatServiceSupabase();
